import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import "./Others.css";
import {
  Settings2,
  Save as SaveIcon,
  Percent,
  Info,
  Calculator,
} from "lucide-react";

// ⚙️ Same fields / keys as before (logic unchanged)
const initialRows = [
  { key: "incidentals", label: "Incidentals", value: "" },
  { key: "markup", label: "Markup", value: "" },
  { key: "commission", label: "Commission", value: "" },
  { key: "finders_fee", label: "Finders Fee", value: "" },
  { key: "tax", label: "Tax (VAT)", value: "" },
  { key: "overhead_rate", label: "Overhead Rate", value: "0.1" },
];

export default function Others() {
  const { companyId } = useCompany();
  const [rows, setRows] = useState(initialRows);
  const [saving, setSaving] = useState(false);

  // 1) Load (UNCHANGED)
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const { data, error } = await supabase
        .from("others")
        .select(
          "company_id,user_id,incidentals,markup,commission,finders_fee,tax,overhead_rate",
        )
        .eq("company_id", companyId)
        .maybeSingle();

      if (error && error.code !== "PGRST116") {
        console.error("Error fetching others:", error);
        return;
      }
      if (data) {
        setRows((prev) =>
          prev.map((r) => ({
            ...r,
            value: data[r.key] != null ? String(data[r.key]) : "",
          })),
        );
      } else {
        setRows(initialRows);
      }
    })();
  }, [companyId]);

  // 2) Handle input (UNCHANGED)
  const handleChange = (idx, newValue) => {
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, value: newValue } : r)),
    );
  };

  // 3) Save (UNCHANGED payload fields & semantics)
  const saveData = async () => {
    if (!companyId) {
      alert("No company selected.");
      return;
    }

    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();
    if (authErr || !user) {
      alert("Please sign in to save.");
      return;
    }
    const userId = user.id;

    // Keep existing behavior: treat inputs as decimal multipliers (0.10 = 10%)
    const pick = (k) => parseFloat(rows.find((r) => r.key === k)?.value) || 0;
    const payload = {
      company_id: companyId,
      user_id: userId,
      incidentals: pick("incidentals"),
      markup: pick("markup"),
      commission: pick("commission"),
      finders_fee: pick("finders_fee"),
      tax: pick("tax"),
      overhead_rate: pick("overhead_rate"),
    };

    try {
      setSaving(true);
      const { data: existing, error: fetchErr } = await supabase
        .from("others")
        .select("company_id")
        .eq("company_id", companyId)
        .maybeSingle();
      if (fetchErr && fetchErr.code !== "PGRST116") throw fetchErr;

      let result;
      if (existing) {
        result = await supabase
          .from("others")
          .update(payload)
          .eq("company_id", companyId);
      } else {
        result = await supabase.from("others").insert([payload]);
      }
      if (result.error) throw result.error;
      alert("Settings saved successfully!");
    } catch (err) {
      console.error("Error saving others:", err);
      alert("Failed to save settings: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // —— UI helpers (visual only; logic unchanged) ——
  const demoBase = 1_000_000; // ₦1,000,000 example base
  const demoImpact = useMemo(() => {
    const asNum = (k) => parseFloat(rows.find((r) => r.key === k)?.value) || 0;
    const inc = demoBase * asNum("incidentals");
    const mupBase = demoBase + inc;
    const mup = mupBase * asNum("markup");
    const price = mupBase + mup;
    const comm = price * asNum("commission");
    const find = price * asNum("finders_fee");
    const preTax = price + comm + find;
    const tax = preTax * asNum("tax");
    const total = preTax + tax;
    return { inc, mup, comm, find, tax, total };
  }, [rows]);

  return (
    <div className="others-shell">
      {/* Header */}
      <header className="panel oth-head">
        <div className="title">
          <h2>
            <Settings2 size={18} /> Cost Settings & Taxes
          </h2>
          <p className="muted">
            Configure multipliers (as decimals) used across Estimates &
            Invoices.
          </p>
        </div>
        <div className="table-actions">
          <button className="btn-primary" onClick={saveData} disabled={saving}>
            <SaveIcon size={16} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </header>

      {/* Hints */}
      <section className="panel oth-hint">
        <p className="muted tiny">
          <Info size={12} /> Enter <strong>decimal values</strong> (e.g.{" "}
          <code>0.10</code> for 10%, <code>0.075</code> for 7.5%). This
          preserves the current pricing logic used elsewhere in the app.
        </p>
      </section>

      {/* Table */}
      <section className="panel table-card">
        <div className="table-wrap">
          <table className="oth-table">
            <thead>
              <tr>
                <th style={{ width: 64 }}>S/N</th>
                <th>Description</th>
                <th style={{ width: 220 }}>
                  <div className="th-flex">
                    <Percent size={14} />
                    <span>Value (decimal)</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.key}>
                  <td>{idx + 1}</td>
                  <td>{r.label}</td>
                  <td>
                    <div className="input-suffix">
                      <input
                        type="number"
                        step="0.001"
                        inputMode="decimal"
                        placeholder="0.10"
                        value={r.value}
                        onChange={(e) => handleChange(idx, e.target.value)}
                      />
                      <span className="suffix">×</span>
                    </div>
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={3} className="empty-state">
                    No settings found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer save (convenience) */}
        <div className="table-actions">
          <button className="btn-primary" onClick={saveData} disabled={saving}>
            <SaveIcon size={16} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </section>

      {/* Example impact */}
      <section className="panel demo-card">
        <div className="demo-head">
          <h3>
            <Calculator size={16} /> Example Impact on ₦
            {demoBase.toLocaleString()}
          </h3>
          <p className="muted tiny">
            This is a visual reference only—your actual totals are computed
            where used.
          </p>
        </div>
        <ul className="demo-grid">
          <li>
            <span>Incidentals</span>
            <strong>+ ₦{demoImpact.inc.toLocaleString()}</strong>
          </li>
          <li>
            <span>Markup</span>
            <strong>+ ₦{demoImpact.mup.toLocaleString()}</strong>
          </li>
          <li>
            <span>Commission</span>
            <strong>+ ₦{demoImpact.comm.toLocaleString()}</strong>
          </li>
          <li>
            <span>Finders Fee</span>
            <strong>+ ₦{demoImpact.find.toLocaleString()}</strong>
          </li>
          <li>
            <span>Tax</span>
            <strong>+ ₦{demoImpact.tax.toLocaleString()}</strong>
          </li>
          <li className="total">
            <span>Illustrative Total</span>
            <strong>₦{demoImpact.total.toLocaleString()}</strong>
          </li>
        </ul>
      </section>
    </div>
  );
}
